/**
 * Localização do profissional para Gestão de vagas (filtro por raio / fila).
 *
 * - Android: getLocation síncrono (serviço de fundo / última posição).
 * - iOS: getLocation via bridge async (GPS fresco + atualiza cache do WebView).
 * - Navegador localhost/192.168 sem app nativo: coordenadas fixas de dev.
 * - Navegador comum: navigator.geolocation.
 */

import { callNativeAsync, isIOS, isNativeApp } from "./config/nativeBridge.js"

/** Centro de Belo Horizonte — apenas dev web (não usar no APK) */
// export const GESTAO_VAGA_LOCALHOST_LATITUDE = -19.932042
// export const GESTAO_VAGA_LOCALHOST_LONGITUDE = -44.156051

export const GESTAO_VAGA_LOCALHOST_LATITUDE = -19.935222
export const GESTAO_VAGA_LOCALHOST_LONGITUDE = -44.160580

const RETRY_TENTATIVAS_GPS = 10
const RETRY_INTERVALO_MS = 1200

function parseRespostaLocalizacaoNativa(raw) {
	if (raw == null || raw === "") {
		return { latitude: 0, longitude: 0 }
	}
	if (typeof raw === "string") {
		try {
			return JSON.parse(raw)
		} catch {
			return { latitude: 0, longitude: 0 }
		}
	}
	return raw
}

function normalizarParLatLon(location) {
	const latitude = Number(location?.latitude) || 0
	const longitude = Number(location?.longitude) || 0
	if (latitude === 0 && longitude === 0) {
		return null
	}
	return { latitude, longitude }
}

function deveUsarCoordenadasFixasDev() {
	return !!window.isLocalHost && !isNativeApp()
}

function localizacaoLocalhost() {
	return {
		latitude: GESTAO_VAGA_LOCALHOST_LATITUDE,
		longitude: GESTAO_VAGA_LOCALHOST_LONGITUDE,
	}
}

function aguardar(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

async function localizacaoNavegador(forcar) {
	if (!navigator?.geolocation?.getCurrentPosition) {
		return null
	}
	return new Promise((resolve) => {
		navigator.geolocation.getCurrentPosition(
			(pos) =>
				resolve(
					normalizarParLatLon({
						latitude: pos.coords.latitude,
						longitude: pos.coords.longitude,
					})
				),
			() => resolve(null),
			{
				enableHighAccuracy: true,
				maximumAge: forcar ? 0 : 60_000,
				timeout: 20_000,
			}
		)
	})
}

async function lerLocalizacaoNativaUmaVez() {
	if (!window.AndroidInterface?.getLocation) {
		return null
	}
	try {
		const raw = await callNativeAsync("getLocation")
		const loc = normalizarParLatLon(parseRespostaLocalizacaoNativa(raw))
		if (loc) {
			return loc
		}
		// iOS: entre retries o locationManager pode ter atualizado o cache do WebView
		if (isIOS() && typeof window.AndroidInterface?.getLocationCached === "function") {
			const cacheRaw = window.AndroidInterface.getLocationCached()
			return normalizarParLatLon(parseRespostaLocalizacaoNativa(cacheRaw))
		}
		return null
	} catch {
		return null
	}
}

/**
 * Lê GPS do app nativo; com forcar, repete como no mapa até obter coords válidas.
 */
async function localizacaoAppNativo(opcoes = {}) {
	const forcar = !!opcoes.forcar
	const tentativas = forcar ? RETRY_TENTATIVAS_GPS : 1

	for (let i = 0; i < tentativas; i += 1) {
		const loc = await lerLocalizacaoNativaUmaVez()
		if (loc) {
			return loc
		}
		if (i < tentativas - 1) {
			await aguardar(RETRY_INTERVALO_MS)
		}
	}
	return null
}

/**
 * Obtém latitude/longitude do profissional.
 * @param {{ forcar?: boolean }} [opcoes] — forcar=true: nova leitura (retries no app nativo)
 * @returns {Promise<{ latitude: number, longitude: number } | null>}
 */
export async function obterLocalizacaoProfissionalGestaoVaga(opcoes = {}) {
	const forcar = !!opcoes.forcar

	// URL 192.168/localhost no Chrome: coords fixas. No APK: nunca substituir por fixas.
	if (deveUsarCoordenadasFixasDev()) {
		return localizacaoLocalhost()
	}

	const nativa = await localizacaoAppNativo({ forcar })
	if (nativa) {
		return nativa
	}

	return localizacaoNavegador(forcar)
}

/** Atualiza a posição (app: retries no getLocation; web dev: fixas; web: GPS). */
export async function atualizarLocalizacaoProfissionalGestaoVaga() {
	return obterLocalizacaoProfissionalGestaoVaga({ forcar: true })
}

/** Parâmetros la/lo para as rotas gestaoVagas* da API. */
export function paramsGeoGestaoVaga(local) {
	if (!local?.latitude || !local?.longitude) {
		return {}
	}
	return {
		la: local.latitude,
		lo: local.longitude,
	}
}
